import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import IconSvg from "./IconSvg";
import "./style.css";

const SalaryRoleAnalysisCard = ({ data, onViewAllRoles }) => {
  const { projectedSalaryDistribution, mostMatchedRoles, insight } = data;
  const chartRef = useRef(null);
  const chartInstance = useRef(null); // Store Chart instance
  const filter_projectedSalaryDistribution = projectedSalaryDistribution.filter(
    (item) => item.count > 0
  );
  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy(); // Destroy previous chart instance
    }

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels: projectedSalaryDistribution.map((d) => d.range),
        datasets: [
          {
            data: projectedSalaryDistribution
              .filter((d) => d.range !== "No match")
              .map((d) => d.count),
            backgroundColor: projectedSalaryDistribution
              .filter((d) => d.range !== "No match")
              .map((d) => d.color),
            borderColor: projectedSalaryDistribution
              .filter((d) => d.range !== "No match")
              .map((d) => d.color.replace("0.7)", "1)")), // Make border opaque
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false, // Allow custom sizing
        plugins: {
          legend: {
            display: false,
          },
        },
      },
    });

    // Cleanup on component unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [projectedSalaryDistribution]);

  return (
    <div className="lg-col-span-4 card p-4">
      <div className="flex justify-between items-start mb-3">
        <span className="font-medium text-gray-800">
          Projected Salary & Role Analysis
        </span>
        <div className="tooltip-wrapper">
          <IconSvg
            path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            className="h-3-5 w-3-5 text-gray-400"
          />
          <div className="tooltip-content w-48">
            Expected salary ranges and most suitable roles based on assessment
            results.
          </div>
        </div>
      </div>

      {/* Salary Pie Chart */}
      <div className="mb-3">
        <h4 className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
          Projected Salary Distribution
          <div className="tooltip-wrapper">
            <IconSvg
              path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              className="h-3-5 w-3-5 text-gray-400"
            />
            <div className="tooltip-content w-48">
              Distribution of students based on their projected salary ranges
              from assessment data, including those with no strong role matches.
            </div>
          </div>
        </h4>
        <div className="flex items-center gap-14">
          <div className="flex items-center justify-center h-40">
            <canvas ref={chartRef} width="160" height="160"></canvas>
          </div>
          <div className="salary-distribution-grid">
            {filter_projectedSalaryDistribution
              .filter((item) => item.range !== "No match")
              .map((item) => (
                <div
                  key={item.range}
                  style={{ backgroundColor: item.color }}
                  className="salary-distribution-item"
                >
                  <div className="salary-range-text">
                    {item.range === "No match" ? item.range : `₹${item.range}`}{" "}
                    {`(${item.count})`}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Most Matched Roles Section */}
      <div className="mb-3">
        <h4 className="text-xs font-medium text-gray-700 flex items-center">
          Most Matched Roles
          <div className="tooltip-wrapper">
            <IconSvg
              path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              className="h-3-5 w-3-5 text-gray-400"
            />
            <div className="tooltip-content w-48">
              Roles with the highest number of student matches based on skill
              evaluation and assessment results.
            </div>
          </div>
        </h4>
        <div className="grid grid-cols-1 gap-2">
          {mostMatchedRoles.map((role) => (
            <div
              key={role.name}
              className="bg-white rounded-xl p-2 relative overflow-hidden border border-gray-200 hover:shadow-sm transition-all"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
              <div className="flex justify-between items-center ml-1">
                <h4 className="font-medium text-gray-800 text-sm">
                  {role.name}
                </h4>
                <span className="matched-role-count-badge">
                  {role.count} students
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key insight */}
      <div className="mt-1 pt-1 border-t border-gray-100">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-indigo-600">Key insight:</span>{" "}
          {insight}
        </p>
      </div>

      {/* View all roles button */}
      <div className="view-all-button-wrapper">
        <button onClick={onViewAllRoles} className="view-all-button">
          <IconSvg
            path="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            className="view-all-button-icon"
          />
          View all roles
        </button>
      </div>
    </div>
  );
};

export default SalaryRoleAnalysisCard;
